<i18n>
{
  "en": {
    "configure": "Customise menu appearance",
    "delete": "Delete",
    "description": "Description",
    "edit": "Edit",
    "font": "Font",
    "gridGenerator": "Grid Generator",
    "menu": "Menu",
    "menus": "Menus",
    "name": "Name",
    "notRevenueManaged": "Revenue Management disabled",
    "preview": "See menu",
    "primaryColor": "Primary Color",
    "printMenuBtn": "Print Menu",
    "publicationDate": "Last publication",
    "publish": "Publish",
    "priority": "Priority",
    "revenueManaged": "Revenue Management enabled",
    "secondaryColor": "Secondary Color",
    "settings": "Settings",
    "unavailable": "Unavailable"
  },
  "es": {
    "configure": "Personalizar apariencia del menú",
    "delete": "Eliminar",
    "description": "Descripción",
    "edit": "Editar",
    "font": "Font",
    "gridGenerator":  "Generador de plantilla",
    "menu": "Menú",
    "menus": "Menús",
    "name": "Nombre",
    "notRevenueManaged": "Revenue Management inactivo",
    "preview": "Ver carta",
    "primaryColor": "Color Primario",
    "printMenuBtn": "Imprimir Menú",
    "priority": "Prioridad",
    "publicationDate": "Última publicación",
    "publish": "Publicar",
    "revenueManaged": "Revenue Management activo",
    "settings": "Ajustes",
    "secondaryColor": "Color Secundario",
    "unavailable": "No disponible"
  }
}
</i18n>

<template>
  <dynameat-row
    :title="menu.title"
    :description="menuDescription"
    :button-config-list="buttonConfigList"
    :collapse-items="!$vuetify.breakpoint.mdAndUp"
    @click-content="onClickContent"
    @preview-publication="onPreviewPublication"
    @edit="onEdit" @delete="onDelete" @configure="onConfigure"
    @publish="onPublish" @settings="onSettings"
    @printMenu="onPrintMenu" @gridConfig="onGridConfig"
    :isClickable="isClickable"
  >
    <template v-slot:rowIconsLeft>
      <v-tooltip top v-if="menu.is_managed"><template v-slot:activator="{ on, attrs }">
        <v-icon v-bind="attrs" v-on="on" color="green" >mdi-currency-usd-circle-outline</v-icon></template><span>{{$t('revenueManaged')}}</span></v-tooltip>
      <v-tooltip top v-else><template v-slot:activator="{ on, attrs }">
        <v-icon v-bind="attrs" v-on="on" color="grey">mdi-currency-usd-circle-outline</v-icon></template><span>{{$t('notRevenueManaged')}}</span></v-tooltip>
    </template>
    <template v-slot:aboveContent>
      <v-row align="center" class="chip-top">
        <v-chip class="chip" x-small>{{ templateName }}</v-chip>
      </v-row>
    </template>
  </dynameat-row>
</template>

<script>

import DynameatRow from '@dynameat/components/DynameatRow';
import Component from 'vue-class-component';
import { Components, MapGetters } from '@core/helpers/decorators';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { Menu } from '@dynameat/views/pointOfSale/models';
import { PermissionController } from '@core/permissions';

@Component
@Components({ DynameatRow })
@MapGetters(['templateList', 'moduleFunctionalities'])
export default class MenuRow extends Vue {
  @Prop({
    Object,
    validator(menu) {
      return menu instanceof Menu;
    },
  }) menu;

  get menuDescription() {
    let datestring = '-';
    if (this.menu.publication) {
      const date = this.menu.publication.modification_date;
      datestring = `${(`0${date.getDate()}`).slice(-2)}-${(`0${date.getMonth() + 1}`).slice(-2)}-${
        date.getFullYear()} ${(`0${date.getHours()}`).slice(-2)}:${(`0${date.getMinutes()}`).slice(-2)}`;
    }
    return `${this.$t('publicationDate')}:
        ${datestring}`;
  }

  get template() {
    return this.templateList.find((temp) => temp.guid === this.menu.template);
  }

  get templateName() {
    return this.template?.name || this.$t('unavailable');
  }

  get hasPublicationDate() {
    return !!this.menu.publication;
  }

  get isMenuTheme() {
    return this.moduleFunctionalities.includes('menuTheme');
  }

  get isMenuEdit() {
    return this.moduleFunctionalities.includes('editMenu');
  }

  get isMenuConfig() {
    return this.moduleFunctionalities.includes('menuConfig');
  }

  get isDeleteMenu() {
    return this.moduleFunctionalities.includes('deleteMenu');
  }

  get isPublishMenu() {
    return this.moduleFunctionalities.includes('publishMenu');
  }

  get isClickable() {
    return PermissionController.getInstance().isAllowed('sectionEditor');
  }

  get showPrintMenuButton() {
    return this.template ? !this.template.isOperationDisallowed('MENU_ACTIONS', 'PRINT_MENU') : false;
  }

  get showGridGenerator() {
    return this.template ? !this.template.isOperationDisallowed('MENU_ACTIONS', 'GRID_GENERATOR') : false;
  }

  get buttonConfigList() {
    const buttons = [];
    if (this.isPublishMenu) {
      buttons.push({
        title: `${this.$t('publish')}`,
        icon: 'mdi-cloud-upload',
        emitName: 'publish'
      });
    }
    if (this.showPrintMenuButton) {
      buttons.push({
        title: `${this.$t('printMenuBtn')}`,
        icon: 'mdi-printer',
        emitName: 'printMenu'
      });
    }
    if (this.showGridGenerator) {
      buttons.push({
        title: `${this.$t('gridGenerator')}`,
        icon: 'mdi-grid',
        emitName: 'gridConfig'
      });
    }
    if (this.isMenuTheme) {
      buttons.push({
        title: `${this.$t('configure')}`,
        icon: 'mdi-palette',
        emitName: 'configure'
      });
    }
    if (this.template?.hasSettings() && this.isMenuConfig) {
      buttons.push({
        title: `${this.$t('settings')}`,
        icon: 'mdi-wrench',
        emitName: 'settings'
      });
    }
    if (this.isMenuEdit) {
      buttons.push({
        title: `${this.$t('edit')}`,
        icon: 'mdi-pencil',
        emitName: 'edit'
      });
    }
    if (!this.menu.is_managed && this.isDeleteMenu) {
      buttons.push(
        {
          title: `${this.$t('delete')}`,
          icon: 'mdi-delete',
          emitName: 'delete'
        },
      );
    }
    if (this.hasPublicationDate) {
      buttons.unshift(
        {
          title: `${this.$t('preview')}`,
          icon: 'mdi-eye',
          emitName: 'preview-publication'
        },
      );
    }
    return buttons;
  }

  onEdit() {
    this.$emit('edit');
  }

  onConfigure() {
    this.$emit('configure');
  }

  onDelete() {
    this.$emit('delete');
  }

  onPublish() {
    this.$emit('publish');
  }

  onClickContent() {
    if (this.isClickable) {
      this.$emit('click-content');
    }
  }

  onPreviewPublication() {
    this.$emit('preview-publication');
  }

  onSettings() {
    this.$emit('settings');
  }

  onPrintMenu() {
    this.$emit('print-menu');
  }

  onGridConfig() {
    this.$emit('grid-config');
  }
}
</script>

<style>
.v-list-item__subtitle {
  white-space: normal;
}
</style>
<style scoped lang="scss">
.chip-top {
  margin-top: 0;
  margin-left: 0;
}

.chip {
  margin-top: 5px;
  margin-right: 5px;
}

.dynameat-row-content {
  cursor: pointer;
}
</style>
